import { Suspense, useMemo } from 'react';
import { Box, Divider, Grid, GridItem, SkeletonText } from '@chakra-ui/react';
import { Colors, Icon, Text } from '@mercator-gcp/grout';
import AIMarker from 'components/elements/AIMarker';
import { EmptyField } from 'components/elements/EmptyField';
import StatusBadge, { StatusTypes } from 'components/elements/StatusBadge';
import Tooltip from 'components/elements/Tooltip';
import { FlexRow } from 'components/layout/FlexRow';
import { DetailGrid } from 'components/widgets/DetailGrid';
import LandTitleRequests from 'components/widgets/LandTitleSection/LandTitleRequests';
import { LocationMap } from 'components/widgets/LocationMap';
import { Lud } from 'components/widgets/Lud';
import { Lurd } from 'components/widgets/Lurd';
import BuildableSqft from 'components/widgets/ProjectDetails/ProjectInfo/BuildableSqft';
import LotSize from 'components/widgets/ProjectDetails/ProjectInfo/LotSize';
import NumberOfFloors from 'components/widgets/ProjectDetails/ProjectInfo/NumberOfFloors';
import NumberOfUnits from 'components/widgets/ProjectDetails/ProjectInfo/NumberOfUnits';
import PricePerSqft from 'components/widgets/ProjectDetails/ProjectInfo/PricePerSqft';
import ProjectAddress from 'components/widgets/ProjectDetails/ProjectInfo/ProjectAddress';
import ProjectSize from 'components/widgets/ProjectDetails/ProjectInfo/ProjectSize';
import ProjectInfoValue from 'components/widgets/ProjectDetails/ProjectInfo/Value';
import WorkType from 'components/widgets/ProjectDetails/ProjectInfo/WorkType';
import StreetViewMap from 'components/widgets/StreetViewMap';
import { Project } from 'types';
import { LandTitle } from 'types/LandTitle';
import { DocumentOverview } from 'types/TempDocument';
import { City } from 'utils/constants/location_legacy';
import { formatCityName } from 'utils/format';
import { cityMap, cityMapId, isCityMatch, titleCase } from 'utils/misc';
import { isNumber } from 'utils/validate';
import type { ProjectSize as ProjectSizeType } from 'types';

const mapStyles = {
  width: '100%',
  height: '280px',
  marginBottom: 'padding_16',
};

const lurdContainer = {
  display: 'inline-flex',
  color: Colors.Tangerine700,
  columnGap: 'padding_8',
};

const containerStyle = {
  width: '100%',
};

const gridStyle = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: 'padding_8',
};

const wideItemStyle = {
  gridColumn: '1 / span 2',
};

const projectOverviewTitleStyle = {
  alignSelf: 'flex-start',
  width: '130px',
};

const MAX_BUILD_AREA_TOOLTIP =
  'Maximum amount of space that can be developed on a given piece of land while adhering to local zoning regulations and building codes. It represents the upper limit of construction size within legal constraints.';

type TProjectInfo = {
  identifier: string | number;
  onMapLoaded?: () => void;
  landTitleHistory?: LandTitle[] | undefined;
  handleTabClick?: (index: number) => void;
  isLoading: boolean;
  project?: Project | DocumentOverview | null;
  projectSizeInfo?: ProjectSizeType | null;
  projectWorkType?: string[];
};

const ProjectInfo = ({
  identifier,
  onMapLoaded,
  landTitleHistory,
  handleTabClick,
  isLoading,
  project,
  projectSizeInfo,
  projectWorkType,
}: TProjectInfo) => {
  const sanitizeStr = (string: string) => string.split(',').join(', ');

  const onMapTilesLoaded = () => {
    if (onMapLoaded) {
      onMapLoaded();
    }
  };

  const projectCity = useMemo(() => {
    const formattedCity = formatCityName(project?.city);

    return String(cityMap[formattedCity as keyof typeof cityMap]);
  }, [project?.city]);

  const shouldShowNumberOfFloorsSection = useMemo(() => {
    return (
      isNumber(projectSizeInfo?.total_floors_min) &&
      isNumber(projectSizeInfo?.total_floors_max) &&
      projectSizeInfo?.total_floors_max !== 0
    );
  }, [projectSizeInfo?.total_floors_min, projectSizeInfo?.total_floors_max]);

  const shouldShowNumberOfUnitsSection = useMemo(() => {
    return (
      isNumber(projectSizeInfo?.total_units_min) &&
      isNumber(projectSizeInfo?.total_units_max) &&
      projectSizeInfo?.total_units_max !== 0
    );
  }, [projectSizeInfo?.total_units_min, projectSizeInfo?.total_units_max]);

  return (
    <SkeletonText sx={containerStyle} isLoaded={!isLoading}>
      <Grid sx={gridStyle}>
        <Box sx={mapStyles}>
          <LocationMap
            mini
            points={[
              {
                lat: project?.latitude!,
                lng: project?.longitude!,
                id: identifier.toString(),
              },
            ]}
            center={{
              lat: project?.latitude!,
              lng: project?.longitude!,
            }}
            mapProps={{
              zoom: 17,
              onTilesLoaded: onMapTilesLoaded,
            }}
            defaultToSateliteView
            highlightCurrentPolygon
            allowPolygonInteraction={false}
            sx={mapStyles}
          />
        </Box>
        <StreetViewMap
          lat={project?.latitude!}
          lng={project?.longitude!}
          height="280px"
        />
        <DetailGrid.Section title="status">
          <StatusBadge status={project?.status as StatusTypes} />
        </DetailGrid.Section>
        {project?.name || project?.summary ? (
          <GridItem sx={wideItemStyle}>
            <DetailGrid.Section
              title="project name"
              labelStyleOverride={projectOverviewTitleStyle}
            >
              {project.name ? (
                <Text.LargeBold as="div">
                  {project.name} <AIMarker inline />
                </Text.LargeBold>
              ) : (
                <Text.Body>-</Text.Body>
              )}
            </DetailGrid.Section>
            <DetailGrid.Section
              title="scope"
              labelStyleOverride={projectOverviewTitleStyle}
            >
              {project?.summary ? (
                <Text.Body>
                  {project.summary}{' '}
                  {Boolean(project.ai_insights?.scope) && <AIMarker inline />}
                </Text.Body>
              ) : (
                <Text.Body>-</Text.Body>
              )}
            </DetailGrid.Section>
          </GridItem>
        ) : null}
        <GridItem sx={wideItemStyle}>
          <DetailGrid.Section title="address">
            <ProjectAddress
              address={project?.address}
              city={project?.city}
              state={project?.state}
              zipcode={project?.zipcode}
            />
          </DetailGrid.Section>
        </GridItem>
        <GridItem sx={wideItemStyle}>
          <DetailGrid.Section title="sectors">
            {(project?.vertical && (
              <Text.Body>{titleCase(sanitizeStr(project?.vertical || ''))}</Text.Body>
            )) || <EmptyField />}
          </DetailGrid.Section>
        </GridItem>
        <GridItem sx={wideItemStyle}>
          <DetailGrid.Section title="Work Type">
            <WorkType workType={projectWorkType || []} />
          </DetailGrid.Section>
        </GridItem>
        {project?.lud_code && (
          <GridItem sx={wideItemStyle}>
            <DetailGrid.Section title="zoned" display="flex" gridGap={1}>
              <Suspense fallback={<EmptyField />}>
                <Lud
                  lud={project?.land_use_redesignation}
                  code={project?.lud_code}
                  cityId={projectCity}
                />
              </Suspense>
            </DetailGrid.Section>
          </GridItem>
        )}

        {(project?.land_use_redesignation?.fromlud ||
          project?.land_use_redesignation?.proposedlud) && (
          <GridItem sx={wideItemStyle}>
            <DetailGrid.Section
              titleOverride={
                <FlexRow sx={lurdContainer}>
                  <Text.SmallLabel>Change to </Text.SmallLabel>
                  <Icon name={Icon.Name.ArrowRight} size={11} />
                </FlexRow>
              }
            >
              <Lurd lud={project?.land_use_redesignation} cityId={projectCity} />
            </DetailGrid.Section>
          </GridItem>
        )}

        <Divider sx={wideItemStyle} my="padding_16" />

        {/* Column 1 */}
        <GridItem>
          {/* 1. TOTAL PROJECT VALUE */}
          <DetailGrid.Section title="total project value">
            <ProjectInfoValue
              cost={project?.project_cost}
              predictedProjectValue={project?.predicted_project_value}
            />
          </DetailGrid.Section>
        </GridItem>

        {/* Column 2 */}
        <GridItem>
          {/* 4. Lot Size */}
          <DetailGrid.Section title="lot size">
            <LotSize lotSizeSqft={projectSizeInfo?.lotsize_sqft} />
          </DetailGrid.Section>
        </GridItem>

        {/* Column 1 */}
        <GridItem>
          {/* 2. Project SQFT */}
          <DetailGrid.Section title="project sqft">
            <ProjectSize
              reportedMinSize={projectSizeInfo?.reported_sqft_min}
              reportedMaxSize={projectSizeInfo?.reported_sqft_max}
              estimatedMinSize={projectSizeInfo?.estimated_sqft_min}
              estimatedMaxSize={projectSizeInfo?.estimated_sqft_max}
            />
          </DetailGrid.Section>
        </GridItem>

        {/* Column 2 */}
        <GridItem>
          {/* 5. Max Buildable area */}
          {!isCityMatch(cityMapId[Number(projectCity)], [
            City.HOUSTON,
            City.SAN_ANTONIO,
            City.DALLAS,
          ]) && (
            <DetailGrid.Section
              title="max. buildable area"
              titleOverride={
                <Tooltip tooltipText={MAX_BUILD_AREA_TOOLTIP}>
                  <Text.SmallLabel color={Colors.TextQuiet}>
                    max. buildable area
                  </Text.SmallLabel>
                </Tooltip>
              }
            >
              <BuildableSqft
                minBuildableSqft={projectSizeInfo?.maxbuildable_sqft_min}
                maxBuildableSqft={projectSizeInfo?.maxbuildable_sqft_max}
              />
            </DetailGrid.Section>
          )}
        </GridItem>

        {/* Column 1 */}
        <GridItem>
          {/* 3. Price per Sqft */}
          <DetailGrid.Section title="Price Per SQFT">
            <PricePerSqft
              projectCost={project?.project_cost}
              reportedMaxSize={projectSizeInfo?.reported_sqft_max}
              estimatedMaxSize={projectSizeInfo?.estimated_sqft_max}
            />
          </DetailGrid.Section>
        </GridItem>

        {/* Column 2 */}
        <GridItem>
          {/* 6. Land Owner */}
          <DetailGrid.Section title="land owner">
            <LandTitleRequests
              landTitleHistory={landTitleHistory}
              handleTabClick={handleTabClick}
            />
          </DetailGrid.Section>
        </GridItem>

        {shouldShowNumberOfFloorsSection && (
          <DetailGrid.Section title="number of floors">
            <NumberOfFloors
              minTotalFloors={projectSizeInfo?.total_floors_min}
              maxTotalFloors={projectSizeInfo?.total_floors_max}
            />
          </DetailGrid.Section>
        )}
        {shouldShowNumberOfUnitsSection && (
          <DetailGrid.Section title="number of units">
            <NumberOfUnits
              minTotalUnits={projectSizeInfo?.total_units_min}
              maxTotalUnits={projectSizeInfo?.total_units_max}
            />
          </DetailGrid.Section>
        )}
      </Grid>
    </SkeletonText>
  );
};

export default ProjectInfo;
